/**
 * ADR-0236 — ops2026 Trust engine: baseline profiles and drift alarms.
 * Read-only. While an alarm is open the engine abstains on that agent's
 * learned behaviour and networks on that site; it closes itself when a
 * new baseline has been learned from post-drift traffic (≥ 7 days).
 */

import type { TrustBaselineSummary } from '@sill/types/api';

import { Card } from '../../components/atoms/Card.js';
import { Pill } from '../../components/atoms/Pill.js';
import { TableScroll } from '../../components/atoms/TableScroll.js';
import { formatCount, formatIsoCompact } from '../../lib/format.js';
import { useC } from '../../lib/theme.js';
import { formatRate } from './trust-helpers.js';

const serifFont = "'Instrument Serif', serif";
const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

export const DRIFT_FEATURE_LABEL: Readonly<Record<string, string>> = {
  asn: 'Networks (ASN)',
  ua_variant: 'User-agent variants',
  path_mix: 'Path mix',
  tls: 'Connection fingerprint',
  header: 'Header shape',
  http_protocol: 'HTTP protocol',
  revisit: 'Revisit interval',
};

export function BaselinesPanel({ b }: { b: TrustBaselineSummary }): JSX.Element {
  const C = useC();
  const kicker = { fontFamily: monoFont, fontSize: 10, letterSpacing: 1.5, color: C.muted, textTransform: 'uppercase' as const, marginBottom: 8 };
  const th = { textAlign: 'left' as const, fontFamily: monoFont, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase' as const, color: C.muted, padding: '6px 8px', borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' as const };
  const td = { padding: '6px 8px', color: C.body, borderBottom: `1px solid ${C.border}` };
  const stat = (label: string, value: string, sub?: string): JSX.Element => (
    <div style={{ minWidth: 140 }}>
      <div style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', color: C.muted }}>{label}</div>
      <div style={{ fontFamily: serifFont, fontSize: 22, color: C.text }}>{value}</div>
      {sub !== undefined ? <div style={{ fontFamily: sansFont, fontSize: 11.5, color: C.muted }}>{sub}</div> : null}
    </div>
  );
  return (
    <>
      <div style={{ ...kicker, marginTop: 24 }}>Baselines and drift</div>
      <Card style={{ padding: 16 }} data-testid="baselines-panel">
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          {stat('Profiles', formatCount(b.profiles), 'site × agent, current')}
          {stat('Behaviour-ready', formatCount(b.profiles_behaviour_ready), 'enough verified history to compare')}
          {stat('Built', b.newest_built_at === null ? '—' : formatIsoCompact(b.newest_built_at), b.stale ? 'stale — the hourly job learns in-run' : 'daily')}
          <div data-testid="open-alarms">{stat('Open drift alarms', formatCount(b.open_alarms), 'engine abstains on these')}</div>
        </div>
        {b.stale ? (
          <div style={{ fontFamily: sansFont, fontSize: 12.5, color: C.amberFg, marginTop: 8 }}>
            No fresh baseline profiles: check job:trust_baseline. Verdicts fall back to learning in the hourly run.
          </div>
        ) : null}
        <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, marginTop: 8 }}>
          Weekly: population stability index per agent and feature against its baseline (alarm above {b.psi_threshold}), and the rate at which verified traffic is accused or flagged with the proof hidden.
          {b.last_check !== null ? <> Last check {formatIsoCompact(b.last_check.created_at)}.</> : <> No check yet.</>}
        </div>
        {b.sampling !== undefined ? (
          <div style={{ fontFamily: sansFont, fontSize: 12, color: b.sampling.readiness_partial_days > 0 || b.sampling.drift_sites_truncated > 0 ? C.amberFg : C.muted, marginTop: 6 }} data-testid="sampling">
            Sampling: {formatCount(b.sampling.profiles_sampled)} profiles learned from a sample · {formatCount(b.sampling.readiness_partial_days)} readiness days partial
            ({formatCount(b.sampling.readiness_truncated_keys)} site × agent inputs too large, left out) · last drift check sampled {formatCount(b.sampling.drift_sampled_keys)} keys
            {b.sampling.drift_sites_truncated > 0 ? `, skipped ${formatCount(b.sampling.drift_sites_truncated)} sites over the read cap` : ''}.
          </div>
        ) : null}
        <div style={{ ...kicker, marginTop: 14 }}>Alarms</div>
        {b.alarms.length === 0 ? (
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>No drift alarms.</div>
        ) : (
          <TableScroll minWidth={760}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: sansFont, fontSize: 12.5 }}>
              <thead>
                <tr>{['Raised', 'Site', 'Agent', 'Kind', 'Value', 'Status'].map((x) => <th key={x} style={th}>{x}</th>)}</tr>
              </thead>
              <tbody>
                {b.alarms.map((a) => (
                  <tr key={a.alarm_id} data-testid={`alarm-${a.alarm_id}`}>
                    <td style={{ ...td, fontFamily: monoFont, fontSize: 11.5 }}>{formatIsoCompact(a.created_at)}</td>
                    <td style={td}>{a.site_domain ?? a.site_id}</td>
                    <td style={td}>{a.agent_name}</td>
                    <td style={td}>
                      {a.kind === 'psi'
                        ? `Behaviour drift · ${DRIFT_FEATURE_LABEL[a.feature ?? ''] ?? a.feature ?? '—'}`
                        : 'Verified traffic flagged more'}
                    </td>
                    <td style={{ ...td, fontFamily: monoFont, fontSize: 11.5 }}>
                      {a.value === null ? '—' : a.kind === 'psi' ? `PSI ${a.value.toFixed(2)}` : formatRate(a.value)}
                    </td>
                    <td style={td}>
                      {a.status === 'open'
                        ? <Pill tone="amber">open · abstaining</Pill>
                        : <Pill tone="neutral">re-baselined {a.rebaselined_at !== null ? formatIsoCompact(a.rebaselined_at) : ''}</Pill>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
        )}
      </Card>
    </>
  );
}
